import type { Asset, Creator, Me, PatronAsset, SlotDef } from "./types";

/**
 * The one seam between screens and data (D-21).
 *
 * Every function maps to exactly one row of plan.md's patron endpoint table.
 * This file's body changed from fixtures to `fetch` at task 22; **no screen
 * file changed with it**, which is what the seam existed to buy.
 *
 * `fixtures.ts` is still here and is now imported only by tests.
 */

export class NotFoundError extends Error {
  constructor(what: string) {
    super(`${what} not found`);
    this.name = "NotFoundError";
  }
}

export class ForbiddenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ForbiddenError";
  }
}

/**
 * No token was available for a route that requires one.
 *
 * Distinct from ForbiddenError: that one means the server considered the
 * caller and refused. This one means the request was never sent.
 */
export class UnauthenticatedError extends Error {
  constructor(what: string) {
    super(`Not signed in: ${what}`);
    this.name = "UnauthenticatedError";
  }
}

let baseUrl = "";
/*
 * The previewed tier, when the owner is previewing - `specs/patron-preview/`.
 *
 * Set by `PreviewProvider` rather than passed per call, for the same reason as
 * the token: threading it through every function would mean touching every
 * screen, and a screen that forgot would silently drop out of preview.
 *
 * It grants nothing. The server honours the header only for the verified owner
 * and ignores it from anyone else (FR-12).
 */
let previewTier: string | null = null;

export function setPreviewTier(tierId: string | null): void {
  previewTier = tierId;
}

/**
 * Called once at start-up.
 *
 * It used to take a `getToken` too. `patreon-direct-auth` task 9 removed it:
 * the session is an `httpOnly` cookie the browser attaches on its own, so
 * there is no token for React to hand over and nothing here that identity
 * changes.
 */
export function configureClient(options: { baseUrl: string }): void {
  baseUrl = options.baseUrl.replace(/\/$/, "");
}

async function request<T>(
  path: string,
  opts: { anonymous?: boolean; method?: string; body?: unknown } = {}
): Promise<T> {
  /*
   * `patreon-direct-auth` task 9: the pre-flight token check is gone, because
   * there is no token to check.
   *
   * It used to refuse locally rather than send a request certain to 401, since
   * API Gateway's 4XX responses carried no CORS headers and the browser
   * discarded them - `fetch` rejected with `TypeError: Failed to fetch` and the
   * status never reached JS, so the error screens said nothing about
   * authentication. Two things changed. The credential is an `httpOnly` cookie
   * this code cannot see, so the check is no longer possible; and task 8 gave
   * those gateway responses the credentialed CORS headers they needed, so a 401
   * is now legible and `RequireSession` keeps signed-out visitors away from
   * these routes anyway.
   *
   * `UnauthenticatedError` survives for the case it was always meant for: a
   * session that expires mid-visit, which now surfaces as a real 401 rather
   * than as an absent token.
   */
  const response = await fetch(`${baseUrl}${path}`, {
    method: opts.method ?? "GET",
    /*
     * The whole change, in one line. Without it the browser sends no cookie
     * and every request is anonymous - and it fails *silently*, as an empty
     * library rather than an error, because that is what "signed in but
     * unrecognised" looks like from a screen's point of view.
     *
     * `include` rather than `same-origin`: the app is on
     * `sbx.perks.tvonguitar.com` and the API on `sbx.api.perks.tvonguitar.com`,
     * which are the same *site* but different origins.
     */
    credentials: "include",
    headers: {
      // Never on an anonymous request: the public route has no caller to
      // verify, so a preview header there could only ever be noise.
      ...(!opts.anonymous && previewTier ? { "X-Preview-Tier": previewTier } : {}),
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
    },
    ...(opts.body ? { body: JSON.stringify(opts.body) } : {}),
  });

  // A 401 is a session that is gone - expired, signed out in another tab, or
  // never there. Distinct from 403, which means the server considered this
  // caller and refused them.
  if (response.status === 401) throw new UnauthenticatedError(path);
  if (response.status === 404) throw new NotFoundError(path);
  if (response.status === 403) throw new ForbiddenError(`Forbidden: ${path}`);
  if (!response.ok) {
    /*
     * A 422 is the server refusing a well-formed request by rule, and its
     * `message` names the rule (`editable-file-slots` FS-D5) - so that is what
     * the creator should read. "HTTP 422" told Adam nothing on 2026-09-30.
     */
    if (response.status === 422) {
      const body = (await response.json().catch(() => null)) as { message?: unknown } | null;
      if (typeof body?.message === "string" && body.message) throw new Error(body.message);
    }
    // The status is part of the message on purpose: the error state shows a
    // mono code line, and "HTTP 502" is more use to a reader than "failed".
    throw new Error(`HTTP ${response.status}`);
  }

  return (await response.json()) as T;
}

/** GET /me - session, resolved tier, isOwner. */
export const getMe = (): Promise<Me> => request<Me>("/me");

/** GET /creator - profile, tiers and lists. The grant matrix is not included. */
export const getCreator = (): Promise<Creator> => request<Creator>("/creator");

/**
 * GET /public/creator - the only route reachable without signing in.
 *
 * Carries the landing's copy plus a published count. No matrix, no assets.
 */
export const getPublicCreator = (): Promise<Creator> =>
  request<Creator>("/public/creator", { anonymous: true });

/** GET /assets - published only, filtered to what the tier may see. */
export const listAssets = (): Promise<PatronAsset[]> =>
  request<PatronAsset[]>("/assets");

/** GET /assets/:slug - 404 if draft or not visible; old slugs redirect (D-11). */
export const getAsset = (slug: string): Promise<PatronAsset> =>
  request<PatronAsset>(`/assets/${encodeURIComponent(slug)}`);

/**
 * GET /assets/:id/files/:slot/url - the gate.
 *
 * The server decides. A refusal arrives here as a ForbiddenError, and the UI's
 * own `gate.ts` only ever decided what to draw (§11.4).
 */
export const getDownloadUrl = async (
  assetId: string,
  slotId: string
): Promise<string> => {
  const result = await request<{ url: string }>(
    `/assets/${encodeURIComponent(assetId)}/files/${encodeURIComponent(slotId)}/url`
  );
  return result.url;
};


/* ---- Studio (owner only) -------------------------------------------------
 * Every one of these is refused server-side unless the caller is OWNER_SUB
 * (D-26). The client-side `isOwner` flag decides what to render and is not a
 * boundary - §11.4.
 * ---------------------------------------------------------------------- */

/** GET /studio/creator - the full profile, matrix included. */
export const getStudioCreator = (): Promise<Creator> =>
  request<Creator>("/studio/creator");

export const saveStudioCreator = (creator: Partial<Creator>): Promise<Creator> =>
  request<Creator>("/studio/creator", { method: "PUT", body: creator });

/** POST /studio/tiers/sync - re-pull from Patreon, stamp tiersSyncedAt. */
export const syncTiers = (): Promise<Creator> =>
  request<Creator>("/studio/tiers/sync", { method: "POST" });

/** GET /studio/assets - everything, drafts included. */
export const listStudioAssets = (): Promise<Asset[]> =>
  request<Asset[]>("/studio/assets");

export const saveAsset = (asset: Asset): Promise<Asset> =>
  request<Asset>(`/studio/assets/${encodeURIComponent(asset.id)}`, {
    method: "PUT",
    body: asset,
  });

/** Soft delete - 30-day window, in-flight downloads finish (D-12). */
export const deleteAsset = (id: string): Promise<{ status: string }> =>
  request<{ status: string }>(`/studio/assets/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

export type UploadTarget = { url: string; key: string };

/**
 * A presigned PUT for one slot. The server signs `ContentLength`, so the
 * upload must be exactly `size` bytes - the 25MB cap is enforced by S3, not by
 * trusting this number.
 */
export const createUploadUrl = (
  assetId: string,
  assetType: string,
  slot: SlotDef,
  file: { name: string; size: number; type: string }
): Promise<UploadTarget> =>
  request<UploadTarget>(
    `/studio/assets/${encodeURIComponent(assetId)}/files/${encodeURIComponent(slot.id)}/upload-url`,
    {
      method: "POST",
      // `assetType` is sent because a new asset has not been saved yet, so the
      // server cannot read its type back. It only *selects* one of the
      // creator's own type definitions - the slot rules and the storage key
      // still come from the server - so it is a lookup key, not a claim.
      body: { assetType, filename: file.name, size: file.size, contentType: file.type },
    }
  );


/**
 * A presigned PUT for a game's cover art — `game-cover-art` FR-5.
 *
 * Owner-only server-side. Same `UploadTarget` and the same 422 rejection shape
 * as the asset route, so `uploadToTarget` and the form's error handling work
 * here unchanged.
 */
export const createGameCoverUploadUrl = (
  gameId: string,
  file: { name: string; size: number; type: string }
): Promise<UploadTarget> =>
  request<UploadTarget>(
    `/studio/games/${encodeURIComponent(gameId)}/cover/upload-url`,
    {
      method: "POST",
      body: { filename: file.name, size: file.size, contentType: file.type },
    }
  );

/**
 * PUT a file at a presigned target, reporting progress and honouring cancel.
 *
 * `fetch` cannot report upload progress - it has no equivalent of
 * `upload.onprogress` - and an in-flight upload has to be cancellable per
 * FR-31, so this is XHR rather than an oversight. It lives here rather than in
 * the form because the screens do not talk to the network (D-21).
 *
 * S3 answers a presigned PUT with an empty 200 and no body, so there is
 * nothing to parse and nothing to return.
 */
export function uploadToTarget(
  target: UploadTarget,
  file: Blob,
  opts: {
    contentType: string;
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
  }
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", target.url, true);
    xhr.setRequestHeader("Content-Type", opts.contentType);

    xhr.upload.onprogress = (e) => {
      // `lengthComputable` is false for a stream of unknown size. A File always
      // knows its size, but the guard keeps a bogus 0% off the screen.
      if (e.lengthComputable && opts.onProgress) {
        opts.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : // S3 explains itself in an XML body. The status is what the row
          // shows, but the body is what makes a signature mismatch legible in
          // the console.
          reject(new Error(`S3 ${xhr.status}${xhr.responseText ? ` · ${xhr.responseText.slice(0, 200)}` : ""}`));

    // A network-level failure carries no detail at all, by design in browsers.
    xhr.onerror = () => reject(new Error("NETWORK OR CORS"));
    xhr.onabort = () => reject(new UploadCancelled());

    opts.signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/** Cancelling is a choice, not a failure - the row must not show an error. */
export class UploadCancelled extends Error {
  constructor() {
    super("Upload cancelled");
    this.name = "UploadCancelled";
  }
}
